import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { Skeleton } from 'antd';
import { requiresAuth, requiresMfaSession } from '@auth/appRouter/requiresAuth';
import { disableCognito } from '@config';
import useStores from '@stores/useStores';
import { observer } from 'mobx-react';
import { RESOURCE_TYPES, SESSION_STORAGE_KEYS } from '@components/common/constants';
import { RudderCategory } from '@components/common/types';
import { apiAuthCaller } from '@services/apiCaller';
import { Path } from '@components/routes/constants';
import { McpAuthorizationPage } from '@components/mcpAuthorizationPage';

import { OAUTH_POST_MESSAGE_TYPE, OAUTH_CHANNEL_NAME } from '../../constants';
import MetaTag from './metaTag';
import { Toast } from './toast';
import { useFeatureFlagObserver } from '@hooks/useFeatureFlagObserver';
import { FeatureFlagKeys } from '@lib/experiment';

const ForgotPasswordPage = lazy(
  () => import(/* webpackChunkName: "auth.forgotPassword" */ '@auth/forgotPassword'),
);
const SSOPage = lazy(() => import(/* webpackChunkName: "auth.sso" */ '@auth/sso'));
const ESignupPage = lazy(
  () => import(/* webpackChunkName: "auth.esignup" */ '@auth/signup/esignup'),
);
const EsignupV2Page = lazy(() =>
  import(/* webpackChunkName: "auth.esignupV2" */ '@authV2/esignup').then((m) => ({
    default: m.EsignupV2,
  })),
);
const Home = lazy(() => import(/* webpackChunkName: "home" */ '@components/home/home'));
const AcceptInvite = lazy(
  () => import(/* webpackChunkName: "auth.acceptInvite" */ '@auth/acceptInvite'),
);
const LoginPage = lazy(() => import(/* webpackChunkName: "auth.login" */ '@auth/login/login'));
const BlendoSignupPage = lazy(
  () => import(/* webpackChunkName: "auth.blendoSignup" */ '@auth/blendo/blendoSignup'),
);
const MfaVerify = lazy(
  () => import(/* webpackChunkName: "auth.mfaVerify" */ '@auth/mfa/mfaVerify'),
);
const AddMfa = lazy(() => import(/* webpackChunkName: "auth.addMfa" */ '@auth/mfa/addMfa'));
const AddMfaLater = lazy(
  () => import(/* webpackChunkName: "auth.addMfaLater" */ '@auth/mfa/addMfaLater'),
);
const AddMfaSuccess = lazy(
  () => import(/* webpackChunkName: "auth.addMfaSuccess" */ '@auth/mfa/addMfaSuccess'),
);
const AddPhonePage = lazy(
  () => import(/* webpackChunkName: "auth.addPhonePage" */ '@auth/addPhonePage'),
);
const TotpVerify = lazy(() =>
  import(/* webpackChunkName: "auth.totpVerify" */ '@auth/mfa/totpVerify').then((m) => ({
    default: m.MfaVerifyPage,
  })),
);
const TotpSetup = lazy(() =>
  import(/* webpackChunkName: "auth.totpSetup" */ '@auth/mfa/totpSetup').then((m) => ({
    default: m.TotpSetupPage,
  })),
);
const Signup = lazy(() => import(/* webpackChunkName: "auth.signup" */ '@auth/signup'));
const SetupAccountV2Flow = lazy(() =>
  import(/* webpackChunkName: "auth.setupAccountV2" */ '@authV2/setupAccountV2').then((m) => ({
    default: m.SetupAccountV2,
  })),
);
const SetupAccountOriginal = lazy(() =>
  import(
    /* webpackChunkName: "auth.setupAccountOriginal" */ '@auth/setupAccount/setupAccount'
  ).then((m) => ({
    default: m.SetupAccountOriginal,
  })),
);
const Security = lazy(() => import(/* webpackChunkName: "security" */ '@components/security'));
const WaitingAuthentication = lazy(
  () => import(/* webpackChunkName: "auth.WaitingAuthentication" */ './WaitingAuthentication'),
);
const SelectOrganization = lazy(
  () => import(/* webpackChunkName: "organizations" */ '@components/organizations'),
);
const UserSuspendedInOrganization = lazy(() =>
  import(
    /* webpackChunkName: "organizations.suspended" */ '@components/organizations/suspended'
  ).then((m) => ({
    default: m.UserSuspendedInOrganization,
  })),
);

interface CallbackRouteParams {
  role: string;
  rudderCategory?: RudderCategory;
}

const OauthRedirect: React.FC<RouteComponentProps> = () => {
  useEffect(() => {
    const { search } = window.location;
    const params = new URLSearchParams(search);
    const code = params.get('code');
    const error = params.get('error');

    const channel = new window.BroadcastChannel(OAUTH_CHANNEL_NAME);
    channel.postMessage({ type: OAUTH_POST_MESSAGE_TYPE, payload: { code, search, error } });
    channel.close();

    return () => {
      channel.close();
    };
  }, []);

  return <WaitingAuthentication />;
};

const AppRouter: React.FC = () => {
  const {
    userStore,
    sourceDefinitionsListStore,
    destinationDefsListStore,
    accountDefinitionsListStore,
  } = useStores();
  const enableMFA = !disableCognito;
  const SignupComponent = disableCognito ? ESignupPage : Signup;
  const isPlgOnboardingV2Enabled = useFeatureFlagObserver(FeatureFlagKeys.enablePlgOnboardingV2);
  const EsignupComponent = isPlgOnboardingV2Enabled ? EsignupV2Page : ESignupPage;
  const SetupAccountComponent = isPlgOnboardingV2Enabled
    ? SetupAccountV2Flow
    : SetupAccountOriginal;

  const authCallback = (props: RouteComponentProps<CallbackRouteParams>) => {
    const oauthCallback = async (compProps: RouteComponentProps<CallbackRouteParams>) => {
      let oauthMetaKey = SESSION_STORAGE_KEYS.source;
      const isDest = compProps!.match.params.rudderCategory === RESOURCE_TYPES.DESTINATION;

      if (isDest) {
        oauthMetaKey = SESSION_STORAGE_KEYS.destination;
      }

      const { pathname } = compProps!.location;
      const OauthMetadata = sessionStorage.getItem(oauthMetaKey);

      let extraQueryParams = {
        t: window.btoa(userStore.token),
        workspaceId: userStore?.selectedWorkspaceId,
      };

      if (OauthMetadata) {
        try {
          const metadataFromStorage = JSON.parse(OauthMetadata);
          extraQueryParams = {
            ...metadataFromStorage,
            ...extraQueryParams,
          };
        } catch (err) {}
      }

      const urlData = await apiAuthCaller({ nonWorkspaceRoute: true }).get(
        `${pathname}${compProps!.location!.search}`,
        { params: extraQueryParams },
      );

      window.location.assign(urlData.data);
    };

    oauthCallback(props);

    return <WaitingAuthentication role={props!.match.params.role} />;
  };

  useEffect(() => {
    const definitionApiCalls = [
      sourceDefinitionsListStore.getAll(),
      destinationDefsListStore.getDestinationDefs(),
      accountDefinitionsListStore.getAccountDefinitions(),
    ];

    Promise.all(definitionApiCalls);
  }, []);

  return (
    <Suspense fallback={<Skeleton active />}>
      <Toast />
      <Security />
      <Router basename={process.env.PUBLIC_URL || '/'}>
        <MetaTag />
        <Switch>
          <Route path="/health">
            <h3>Hey There!!! The App is Healthy</h3>
          </Route>
          <Route path="/authorize" exact component={McpAuthorizationPage} />
          <Route path="/blendoSignup" exact component={BlendoSignupPage} />
          <Route path={Path.SIGNUP} exact component={SignupComponent} />
          <Route path={Path.LOGIN} exact component={LoginPage} />
          <Route path="/esignup" exact component={EsignupComponent} />
          <Route path="/elogin" exact component={LoginPage} />
          <Route path="/sso" exact component={SSOPage} />
          <Route path="/oauth" exact component={OauthRedirect} />
          <Route path="/auth/:role/callback" exact component={authCallback} />
          <Route path="/auth/:rudderCategory/:role/callback" exact component={authCallback} />
          <Route path="/oauth/success/:id" exact component={WaitingAuthentication} />
          <Route path="/oauth/error/:message" exact component={WaitingAuthentication} />
          <Route path="/forgotPassword" exact component={ForgotPasswordPage} />
          <Route path="/acceptInvite" exact component={AcceptInvite} />
          {enableMFA && <Route exact path="/addmfa" component={requiresMfaSession(AddMfa)} />}
          {enableMFA && <Route exact path="/addmfalater" component={requiresAuth(AddMfaLater)} />}
          {enableMFA && (
            <Route exact path="/addmfasuccess" component={requiresMfaSession(AddMfaSuccess)} />
          )}
          {enableMFA && (
            <Route exact path="/addphone" component={requiresMfaSession(AddPhonePage)} />
          )}
          {enableMFA && (
            <Route exact path="/verifyphone" component={requiresMfaSession(MfaVerify)} />
          )}
          {enableMFA && (
            <Route exact path="/verify-totp" component={requiresMfaSession(TotpVerify)} />
          )}
          {enableMFA && (
            <Route exact path="/setup-totp" component={requiresMfaSession(TotpSetup)} />
          )}
          <Route path={Path.ACCOUNT_SETUP} exact component={requiresAuth(SetupAccountComponent)} />
          <Route
            path={Path.USER_SUSPENDED}
            exact
            component={requiresAuth(UserSuspendedInOrganization)}
          />
          <Route path={Path.SELECT_ORGANIZATION} component={requiresAuth(SelectOrganization)} />
          <Route path="/" component={requiresAuth(Home)} />
        </Switch>
      </Router>
    </Suspense>
  );
};

/* eslint-disable import/no-default-export */
export default observer(AppRouter);
